/**
 * Analytics Plugin - Captures and reports analytics events
 * Monitors CDF analytics events sent through the application
 */

import { AnalyticsEvent, EchoConnection, EchoPlugin, EchoTableRow } from '../types'

// EchoClient will be imported from data-access when we move it there
// For now, we'll use a minimal interface to avoid circular dependencies
interface EchoClientLike {
  emit(event: 'analytics_event', data: AnalyticsEvent): void
}

export class AnalyticsPlugin implements EchoPlugin {
  readonly id = 'com.echo.plugin.analytics'
  private connection: EchoConnection | null = null
  private echoClient: EchoClientLike
  private events: AnalyticsEvent[] = []
  private isActive = false

  constructor(echoClient: EchoClientLike) {
    this.echoClient = echoClient
    this.setupAnalyticsInterception()
  }

  onConnect(connection: EchoConnection): void {
    this.connection = connection
    this.isActive = true
    this.flushEvents()
  }

  onDisconnect(): void {
    this.connection = null
    this.isActive = false
  }

  onDesktopPluginActive(): void {
    this.isActive = true
    this.flushEvents()
  }

  onDesktopPluginInactive(): void {
    this.isActive = false
  }

  onDataReceived(data: string): void {
    try {
      const parsedData = JSON.parse(data)

      // Handle search queries from desktop plugin
      if (parsedData.type === 'search' && parsedData.query) {
        this.handleSearch(parsedData.query)
      }
    } catch {
      // Invalid data format, ignore
    }
  }

  /**
   * Record an analytics event
   */
  trackEvent(name: string, parameters: Record<string, unknown> = {}, source = 'web'): void {
    const event: AnalyticsEvent = {
      id: `${Date.now()}-${Math.random()}`,
      name,
      parameters,
      timestamp: Date.now(),
      source,
    }

    this.events.push(event)

    // Keep only last 500 events to prevent memory issues
    if (this.events.length > 500) {
      this.events.shift()
    }

    // Emit event for external listeners
    this.echoClient.emit('analytics_event', event)

    // Send to Echo if connected and active
    if (this.isActive && this.connection) {
      this.sendEvent(event)
    }
  }

  /**
   * Get all tracked events (for external access)
   */
  getEvents(): AnalyticsEvent[] {
    return [...this.events]
  }

  /**
   * Clear all tracked events
   */
  clearEvents(): void {
    this.events = []
  }

  /**
   * Filter events by search query
   */
  private handleSearch(query: string): void {
    if (!this.connection) return

    const filteredEvents = this.events.filter(
      event =>
        event.name.toLowerCase().includes(query.toLowerCase()) ||
        JSON.stringify(event.parameters).toLowerCase().includes(query.toLowerCase())
    )

    // Send filtered results
    filteredEvents.forEach(event => this.sendEvent(event))
  }

  /**
   * Setup interception of analytics events
   */
  private setupAnalyticsInterception(): void {
    // Intercept CDF logEvent calls if available
    const originalPost = window.fetch
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this

    window.fetch = async function (...args: Parameters<typeof fetch>) {
      const [url, options] = args

      // Check if this is a CDF analytics request
      if (typeof url === 'string' && url.includes('/cdp') && options?.method === 'POST') {
        try {
          const body = options.body
          if (typeof body === 'string') {
            const data = JSON.parse(body)

            // Extract CDF events from the request
            if (data.analytics_messages) {
              data.analytics_messages.forEach((message: { track?: { event_name: string; properties?: string } }) => {
                if (message.track) {
                  const properties = message.track.properties ? JSON.parse(message.track.properties) : {}

                  self.trackEvent(message.track.event_name, properties, 'cdf')
                }
              })
            }
          }
        } catch {
          // Failed to parse or extract analytics, continue normally
        }
      }

      return originalPost.apply(this, args)
    }
  }

  /**
   * Send event to Echo desktop
   */
  private sendEvent(event: AnalyticsEvent): void {
    if (!this.connection) return

    // Format as EchoTableRow structure matching Swift version
    const tableRow: EchoTableRow = {
      id: event.id,
      columnItems: {
        Time: new Date(event.timestamp).toISOString(),
        Source: event.source,
        Event: event.name,
        'Raw Message': JSON.stringify(event),
        Properties: JSON.stringify(event.parameters, null, 2),
      },
    }

    this.connection.send(JSON.stringify(tableRow))
  }

  /**
   * Flush all buffered events
   */
  private flushEvents(): void {
    if (!this.connection || this.events.length === 0) return

    this.events.forEach(event => this.sendEvent(event))
  }
}
